import { Link, useNavigate } from "react-router-dom";
import Navbar from "../home/components/Navbar";
import Footer from "../home/components/Footer";
import SEO from "../../components/base/SEO";

const stats = [
  { value: "5.0★", label: "Google Rating" },
  { value: "20+", label: "Years Experience" },
];


const whyUs = [
  {
    icon: "ri-shield-check-line",
    title: "Licensed & Insured",
    desc: "Every technician on our team is fully licensed in Connecticut, and we are fully Insured.",
  },
  {
    icon: "ri-money-dollar-circle-line",
    title: "Upfront, Honest Pricing",
    desc: "We quote before we work. No surprise charges, no pressure upsells. Our technicians are salaried, not commission-based.",
  },
  {
    icon: "ri-time-line",
    title: "We Show Up On Time",
    desc: "We respect your schedule. Arrival windows are honored, and you'll get a call when your tech is on the way.",
  },
  {
    icon: "ri-tools-line",
    title: "All Brands, All Systems",
    desc: "From Carrier and Lennox to Trane, Bryant, and Mitsubishi — our team services and installs everything.",
  },
  {
    icon: "ri-home-heart-line",
    title: "Connecticut-Based & Proud",
    desc: "We live here, work here, and care about our reputation in this community. Every job is personal to us.",
  },
];

const serviceAreas = [
  "Somers", "Enfield", "Stafford", "Windsor", "Suffield",
  "East Windsor", "Ellington", "Tolland", "Vernon", "Coventry",
  "Mansfield", "Willington", "Union", "Woodstock", "Putnam",
];

const certifications = [
  { icon: "ri-leaf-line", label: "Energy Star Partner" },
  { icon: "ri-medal-line", label: "EPA 608 Certified" },
  { icon: "ri-verified-badge-line", label: "CT State Licensed" },
  { icon: "ri-star-fill", label: "BBB Accredited A+" },
];

const siteUrl: string = (import.meta.env.VITE_SITE_URL as string) ?? '';

const aboutJsonLd = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  "name": "About Ultimate Heating & Cooling | Connecticut HVAC Company Since 2009",
  "description": "Learn about Ultimate Heating & Cooling — founded in 2009 in Somers, CT. 15+ years serving Connecticut homeowners with NATE-certified technicians, honest pricing, and emergency HVAC service.",
  "url": `${siteUrl}/about`,
  "mainEntity": {
    "@type": "HVACBusiness",
    "name": "Ultimate Heating & Cooling",
    "foundingDate": "2009",
    "numberOfEmployees": { "@type": "QuantitativeValue", "value": 30 },
    "telephone": "+19592418524",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Somers",
      "addressRegion": "CT",
      "addressCountry": "US"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "5.0",
      "bestRating": "5",
      "reviewCount": "500"
    }
  },
  "breadcrumb": {
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": siteUrl },
      { "@type": "ListItem", "position": 2, "name": "About Us", "item": `${siteUrl}/about` }
    ]
  }
};

export default function AboutPage() {
  const navigate = useNavigate();

  const handleGetQuote = () => {
    sessionStorage.setItem('scrollTarget', 'contact');
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="About Us | Ultimate Heating & Cooling | Connecticut HVAC Company Since 2009"
        description="Ultimate Heating & Cooling has served Connecticut homeowners since 2009. 30+ NATE-certified technicians, honest pricing, and emergency HVAC service in Hartford & Tolland County."
        keywords="about Ultimate Heating Cooling, Connecticut HVAC company history, NATE certified HVAC team, local HVAC company Somers CT"
        canonical="/about"
        jsonLd={aboutJsonLd}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative w-full h-[420px] overflow-hidden">
        <img
          src="https://static.readdy.ai/image/36fff7c058435a92029a1d7ae462693c/0cc4989b336269123d96ef852f57a1b4.jpeg"
          alt="Ultimate Heating & Cooling team"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B2447]/95 via-[#0B2447]/75 to-[#0B2447]/20"></div>
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-6xl mx-auto px-6 w-full">
            <span className="inline-block bg-[#C8102E] text-white text-[10px] font-bold tracking-[0.2em] px-3 py-1.5 rounded-full mb-4">ABOUT US</span>
            <h1 className="text-4xl md:text-5xl font-extrabold text-white leading-tight mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Connecticut&apos;s HVAC Team<br />You Can Actually Trust
            </h1>
            <p className="text-white/75 text-base max-w-xl leading-relaxed">
              Family-built, locally owned, and serving Connecticut homeowners since 2009 — one honest job at a time.
            </p>
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <section className="bg-[#C8102E] py-8">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex justify-center gap-16 text-center">
            {stats.map((s) => (
              <div key={s.label}>
                <div className="text-3xl font-extrabold text-white" style={{ fontFamily: "'Montserrat', sans-serif" }}>{s.value}</div>
                <div className="text-white/75 text-xs font-semibold tracking-wider uppercase mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-14 items-center">
            <div className="relative">
              <div className="w-full h-[400px] rounded-2xl overflow-hidden">
                <img
                  src="https://static.readdy.ai/image/36fff7c058435a92029a1d7ae462693c/3bf11400bc67312a8a53add63bc060d6.jpeg"
                  alt="Paul - Founder of Ultimate Heating & Cooling"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-5 -right-4 bg-[#0B2447] text-white px-5 py-4 rounded-xl">
                <div className="text-2xl font-extrabold text-[#C8102E]">2025</div>
                <div className="text-[10px] font-bold tracking-wider text-gray-300 uppercase">Founded</div>
              </div>
            </div>

            <div>
              <span className="text-[#C8102E] text-xs font-bold tracking-[0.2em] uppercase">OUR STORY</span>
              <h2 className="text-3xl font-extrabold text-[#0B2447] mt-3 mb-5 leading-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                Family Owned &amp; Operated
              </h2>
              <p className="text-gray-500 text-sm leading-relaxed mb-4">
                Ultimate Heating &amp; Cooling is family owned, and operated, we are a small HVAC business located in Tolland County Connecticut, which we proudly serve.&nbsp;
              </p>
              <button
                onClick={handleGetQuote}
                className="inline-block bg-[#C8102E] text-white font-bold text-sm px-8 py-3 rounded-full hover:bg-[#A8091D] transition-colors cursor-pointer whitespace-nowrap"
              >
                GET A FREE QUOTE
              </button>
            </div>
          </div>
        </div>
      </section>


      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="text-[#C8102E] text-xs font-bold tracking-[0.2em] uppercase">WHY ULTIMATE</span>
            <h2 className="text-3xl font-extrabold text-[#0B2447] mt-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              What Sets Us Apart
            </h2>
            <p className="text-gray-500 text-sm max-w-lg mx-auto mt-3 leading-relaxed">
              There&apos;s no shortage of HVAC companies in Connecticut. Here&apos;s why homeowners choose Ultimate — and keep coming back.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {whyUs.map((item, idx) => (
              <div key={item.title} className={`border border-gray-100 rounded-xl p-7 hover:border-[#C8102E]/30 transition-colors${idx === 4 ? ' md:col-start-3' : ''}`}>
                <div className="w-12 h-12 flex items-center justify-center bg-[#C8102E]/10 rounded-xl mb-5">
                  <i className={`${item.icon} text-2xl text-[#C8102E]`}></i>
                </div>
                <h3 className="font-extrabold text-[#0B2447] text-sm mb-2">{item.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-16 bg-[#0B2447]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-10">
            <span className="text-[#C8102E] text-xs font-bold tracking-[0.2em] uppercase">CREDENTIALS</span>
            <h2 className="text-2xl font-extrabold text-white mt-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Certified, Licensed &amp; Accredited
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {certifications.map((cert) => (
              <div key={cert.label} className="bg-white/5 border border-white/10 rounded-xl p-5 text-center hover:bg-white/10 transition-colors">
                <div className="w-10 h-10 flex items-center justify-center bg-[#C8102E]/20 rounded-full mx-auto mb-3">
                  <i className={`${cert.icon} text-[#C8102E] text-lg`}></i>
                </div>
                <p className="text-white text-[11px] font-bold leading-tight">{cert.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-20 bg-[#F0F4FA]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-10">
            <span className="text-[#C8102E] text-xs font-bold tracking-[0.2em] uppercase">WHERE WE WORK</span>
            <h2 className="text-3xl font-extrabold text-[#0B2447] mt-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Proudly Serving Connecticut
            </h2>
            <p className="text-gray-500 text-sm max-w-lg mx-auto mt-3 leading-relaxed">
              Based in Somers, CT — we serve communities across Hartford County, Tolland County, and surrounding areas.
            </p>
          </div>
          <div className="mt-4 rounded-xl overflow-hidden border-4 border-[#C8102E]">
            <img
              src="https://storage.readdy-site.link/project_files/f6c24b4f-84f4-4d51-a5a2-519060acd333/2aa3bb18-2279-497e-979f-795b8fcf7efa_ChatGPT-Image-Mar-20-2026-11_35_17-PM.png?v=afc19f88fd77a08bbc902d2875c3ef86"
              alt="Connecticut service area map"
              className="w-full h-auto block"
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <span className="text-[#C8102E] text-xs font-bold tracking-[0.2em] uppercase">READY TO START?</span>
          <h2 className="text-3xl font-extrabold text-[#0B2447] mt-3 mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Let&apos;s Take Care of Your Home
          </h2>
          <p className="text-gray-500 text-sm leading-relaxed max-w-lg mx-auto mb-8">
            Whether it&apos;s a repair, a replacement, or just a tune-up — our team is ready. Get a free quote today with no pressure and no obligation.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={handleGetQuote}
              className="bg-[#C8102E] text-white font-bold text-sm px-10 py-3.5 rounded-full hover:bg-[#A8091D] transition-colors cursor-pointer whitespace-nowrap"
            >
              GET A FREE QUOTE
            </button>
            <a
              href="tel:9592418524"
              className="border-2 border-[#0B2447] text-[#0B2447] font-bold text-sm px-10 py-3.5 rounded-full hover:bg-[#0B2447] hover:text-white transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-phone-fill"></i> (959) 241-8524
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
